// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { cn } from '@/lib/utils';
import type { UserListItem } from '@greenlight/shared';

import { AppAccessCluster } from './AppAccessCluster';
import { GroupChip } from './GroupChip';
import { UserAvatar } from '@/components/greenlight/UserAvatar';

export interface UserRowProps {
  user: UserListItem;
  onInspect: (user: UserListItem) => void;
}

/**
 * A single user row in the org directory list. Responsive in three tiers:
 * single-column card on phones, two-column card on sm–lg, 4-column grid on lg+.
 * Keyboard-activatable: Enter and Space both trigger onInspect.
 */
export function UserRow({ user, onInspect }: UserRowProps): React.JSX.Element {
  const displayName = user.display_name ?? user.email;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter') {
      onInspect(user);
    } else if (e.key === ' ') {
      e.preventDefault(); // prevent scroll
      onInspect(user);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onInspect(user)}
      onKeyDown={handleKeyDown}
      aria-label={`Inspect ${displayName}`}
      className={cn(
        'group cursor-pointer px-4 md:px-5 py-4 transition-colors hover:bg-accent/50 outline-none focus-visible:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        'flex flex-col gap-2.5',
        // Desktop (lg+): table-style 4-column grid. Below lg it stays a stacked
        // card so tablet widths don't leave a wide empty app-access column.
        'lg:grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1.5fr)_minmax(0,1fr)_16px]',
        'lg:items-center lg:gap-x-6 lg:gap-y-0',
      )}
    >
      {/* Column 1 — Identity */}
      <div className="flex items-center gap-3.5 min-w-0">
        <UserAvatar name={displayName} size={42} deactivated={!user.is_active} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'text-[15px] font-semibold leading-tight truncate',
                !user.is_active && 'text-muted-foreground',
              )}
            >
              {displayName}
            </span>
            {!user.is_active && <StatusBadge variant="inactive">Deactivated</StatusBadge>}
          </div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground truncate">{user.email}</div>
        </div>
      </div>

      {/* Lower section — groups + app access.
          Card (<lg): one column on narrow phones, two once there's room (sm+).
          Desktop (lg+): display:contents so each block flows into the row grid. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 min-w-0 lg:contents">
        {/* Groups */}
        <div className="grid grid-cols-[88px_1fr] items-center gap-x-3 lg:flex lg:items-center lg:gap-1.5 lg:flex-wrap min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.07em] text-muted-foreground shrink-0 whitespace-nowrap lg:hidden">
            Groups
          </span>
          <div className="flex items-center gap-1.5 flex-wrap lg:contents">
            {user.groups && user.groups.length > 0 ? (
              user.groups.map((g) => <GroupChip key={g.id} group={g} />)
            ) : (
              <span className="font-mono text-[12px] text-muted-foreground">—</span>
            )}
          </div>
        </div>

        {/* App access */}
        <div className="grid grid-cols-[88px_1fr] items-center gap-x-3 lg:flex lg:items-center lg:gap-3 min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.07em] text-muted-foreground shrink-0 whitespace-nowrap lg:hidden">
            App access
          </span>
          <div className="min-w-0 lg:contents">
            <AppAccessCluster user={user} />
          </div>
        </div>
      </div>

      {/* Chevron — desktop only */}
      <ChevronRight
        className="hidden lg:block h-4 w-4 text-muted-foreground transition-colors group-hover:text-foreground justify-self-end"
        strokeWidth={2}
        aria-hidden
      />
    </div>
  );
}
